import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formationService } from '../services/formationService';
import './FormationDetailPage.css';

const FormationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formation, setFormation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('particulier'); // 'particulier' ou 'entreprise'

  // État du formulaire
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    entreprise: '', // Pour l'onglet entreprise
    poste: '', // Fonction du responsable (entreprise)
    nombrePersonnes: 1,
    type: 'particulier'
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Récupérer les détails de la formation via le service (bonne URL + bon endpoint)
  useEffect(() => {
    const fetchFormation = async () => {
      try {
        const data = await formationService.getFormation(id);

        if (data.success && data.formation) {
          setFormation(data.formation);
        } else {
          setFormation(null);
        }
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement de la formation:', error);
        setFormation(null);
        setLoading(false);
      }
    };

    fetchFormation();
  }, [id]);

  // Gérer le changement d'onglet
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFormData({
      ...formData,
      type: tab,
      nombrePersonnes: 1
    });
    setErrors({});
  };

  // Gérer les changements de formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  // Calculer le prix total pour les entreprises
  const calculateTotalPrice = () => {
    if (activeTab === 'entreprise' && formation?.prix_entreprise) {
      const prixUnitaire = parseInt(formation.prix_entreprise);
      const nombrePersonnes = parseInt(formData.nombrePersonnes) || 1;
      return prixUnitaire * nombrePersonnes;
    }
    return parseInt(formation?.prix_particulier || 0);
  };

  // Valider le formulaire
  const validateForm = () => {
    const newErrors = {};

    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom complet est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    if (activeTab === 'entreprise') {
      if (!formData.entreprise.trim()) {
        newErrors.entreprise = 'Le nom de l\'entreprise est requis';
      }

      if (!formData.poste.trim()) {
        newErrors.poste = 'La fonction du responsable est requise';
      }

      if (!formData.nombrePersonnes || formData.nombrePersonnes < 1) {
        newErrors.nombrePersonnes = 'Le nombre de personnes doit être au moins 1';
      }

      if (formData.nombrePersonnes > 50) {
        newErrors.nombrePersonnes = 'Pour plus de 50 personnes, contactez-nous directement';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Mapping des champs vers le format attendu par le backend (api.php -> create_inscription)
      const inscriptionData = activeTab === 'entreprise'
        ? {
            formation_id: id,
            type: 'entreprise',
            nom_entreprise: formData.entreprise,
            email_entreprise: formData.email,
            telephone: formData.telephone,
            nombre_participants: parseInt(formData.nombrePersonnes) || 1,
            nom_responsable: formData.nom,
            poste_responsable: formData.poste
          }
        : {
            formation_id: id,
            type: 'particulier',
            nom_complet: formData.nom,
            email: formData.email,
            telephone: formData.telephone
          };

      const result = await formationService.createInscription(inscriptionData);

      if (result.success) {
        navigate(`/payment/${result.inscription_id}`);
      } else {
        throw new Error(result.message || 'Erreur lors de l\'inscription');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue lors de l\'inscription. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="formation-detail-loading">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  if (!formation) {
    return (
      <div className="formation-detail-error">
        <h2>Formation non trouvée</h2>
        <button onClick={() => navigate('/formations')}>
          Retour aux formations
        </button>
      </div>
    );
  }

  return (
    <div className="formation-detail-page">
      <div className="formation-detail-container">
        {/* Section gauche - Détails de la formation */}
        <div className="formation-detail-left">
          <button
            className="back-button"
            onClick={() => navigate('/formations')}
          >
            ← Retour aux formations
          </button>

          <h1 className="formation-title">
            📚 À propos de cette formation
          </h1>

          <div className="formation-image">
            {formation?.image_url ? (
              <img
                src={formation.image_url}
                alt={formation?.titre || 'Formation'}
              />
            ) : (
              <div className="placeholder-image">
                🎓
              </div>
            )}
          </div>

          <h2>{formation?.titre || 'Titre de la formation'}</h2>

          <div
            className="formation-description"
            dangerouslySetInnerHTML={{ __html: formation?.description || '' }}
          />

          <div className="formation-info">
            {formation.duree && (
              <div className="info-item">
                <strong>⏱️ Durée:</strong> {formation.duree}
              </div>
            )}
            {formation.niveau && (
              <div className="info-item">
                <strong>📊 Niveau:</strong> {formation.niveau}
              </div>
            )}
            {formation.formateur && (
              <div className="info-item">
                <strong>👨‍🏫 Formateur:</strong> {formation.formateur}
              </div>
            )}
            {formation.lieu && (
              <div className="info-item">
                <strong>📍 Lieu:</strong> {formation.lieu}
              </div>
            )}
          </div>
        </div>

        {/* Section droite - Formulaire d'inscription */}
        <div className="formation-detail-right">
          <div className="inscription-card">
            {/* Onglets Particulier / Entreprise */}
            <div className="tabs">
              <button
                className={`tab ${activeTab === 'particulier' ? 'active' : ''}`}
                onClick={() => handleTabChange('particulier')}
              >
                Particulier
              </button>
              <button
                className={`tab ${activeTab === 'entreprise' ? 'active' : ''}`}
                onClick={() => handleTabChange('entreprise')}
              >
                Entreprise
              </button>
            </div>

            {/* Prix */}
            <div className="price-section">
              <p className="price-label">PRIX :</p>
              {activeTab === 'entreprise' && formData.nombrePersonnes > 1 ? (
                <>
                  <p className="price-detail">
                    {parseInt(formation?.prix_entreprise || 0).toLocaleString()} FCFA × {formData.nombrePersonnes} personne{formData.nombrePersonnes > 1 ? 's' : ''}
                  </p>
                  <p className="price-total">
                    {calculateTotalPrice().toLocaleString()} FCFA
                  </p>
                </>
              ) : (
                <p className="price">
                  {activeTab === 'particulier'
                    ? parseInt(formation?.prix_particulier || 0).toLocaleString()
                    : parseInt(formation?.prix_entreprise || 0).toLocaleString()
                  } FCFA
                </p>
              )}
            </div>

            {/* Formulaire */}
            <form onSubmit={handleSubmit} className="inscription-form">
              <h3>Inscription</h3>

              {/* Champ Nom complet / Nom du responsable */}
              <div className="form-group">
                <label htmlFor="nom">
                  {activeTab === 'entreprise' ? 'Nom du responsable' : 'Nom complet'} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="nom"
                  name="nom"
                  value={formData.nom}
                  onChange={handleInputChange}
                  placeholder="Votre nom complet"
                  className={errors.nom ? 'error' : ''}
                />
                {errors.nom && <span className="error-message">{errors.nom}</span>}
              </div>

              {/* Champ Email */}
              <div className="form-group">
                <label htmlFor="email">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="votre@email.com"
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              {/* Champs spécifiques à l'entreprise */}
              {activeTab === 'entreprise' && (
                <>
                  <div className="form-group">
                    <label htmlFor="entreprise">
                      Nom de l'entreprise <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="entreprise"
                      name="entreprise"
                      value={formData.entreprise}
                      onChange={handleInputChange}
                      placeholder="Nom de votre entreprise"
                      className={errors.entreprise ? 'error' : ''}
                    />
                    {errors.entreprise && (
                      <span className="error-message">{errors.entreprise}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="poste">
                      Fonction du responsable <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="poste"
                      name="poste"
                      value={formData.poste}
                      onChange={handleInputChange}
                      placeholder="Ex: Directeur RH, Responsable Formation..."
                      className={errors.poste ? 'error' : ''}
                    />
                    {errors.poste && (
                      <span className="error-message">{errors.poste}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="nombrePersonnes">
                      Nombre de personnes <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      id="nombrePersonnes"
                      name="nombrePersonnes"
                      min="1"
                      max="50"
                      value={formData.nombrePersonnes}
                      onChange={handleInputChange}
                      placeholder="Nombre de participants"
                      className={errors.nombrePersonnes ? 'error' : ''}
                    />
                    {errors.nombrePersonnes && (
                      <span className="error-message">{errors.nombrePersonnes}</span>
                    )}
                    <span className="field-hint">
                      Pour plus de 50 personnes, contactez-nous pour un devis personnalisé
                    </span>
                  </div>
                </>
              )}

              {/* Champ Téléphone (optionnel) */}
              <div className="form-group">
                <label htmlFor="telephone">Téléphone</label>
                <input
                  type="tel"
                  id="telephone"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleInputChange}
                  placeholder="+225 XX XX XX XX XX"
                />
              </div>

              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Inscription en cours...' : 'S\'inscrire à la formation'}
              </button>

              <p className="form-footer">
                En vous inscrivant, vous acceptez nos conditions générales
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormationDetailPage;